import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from 'utils/tailwind';

export const typographyVariants = cva('text-primary-black', {
  variants: {
    variant: {
      xxl: 'text-[5.25rem] leading-21 tracking-[-.36rem] uppercase font-black',
      xl: 'text-[4rem] leading-16 tracking-[-.24rem] font-bold',
      l: 'text-[3rem] leading-12 tracking-[-.18rem] font-bold',
      m: 'text-[2.25rem] leading-10 tracking-[-.135rem] font-bold',
      s: 'text-[1.75rem] leading-8 tracking-[-.105rem] font-semibold',
      xs: 'text-[1.5rem] leading-7 tracking-[-.09rem] font-bold',
      xxs: 'text-[1.25rem] leading-6 tracking-[-.05rem] font-semibold',
      'text-3xl': 'text-[1.875rem] leading-9 tracking-[-.075rem]',
      'text-2xl': 'text-[1.5rem] leading-8 tracking-[-.06rem]',
      'text-l': 'text-[1.125rem] leading-6 tracking-[-.03375rem]',
      'text-m': 'text-[1rem] leading-6 tracking-[-.02rem]',
      'text-s': 'text-[.875rem] leading-5 tracking-[-.00875rem]',
      'text-xs': 'text-[.75rem] leading-5 tracking-[-.00875rem]',
    },
    affects: {
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      link: 'underline',
    },
  },
  defaultVariants: {
    variant: 'text-m',
  },
});

type HTMLTypography = HTMLHeadingElement | HTMLParagraphElement;

export type TypographyProps = VariantProps<typeof typographyVariants> &
  (HTMLAttributes<HTMLTypography> | HTMLAttributes<HTMLSpanElement>) & {
    tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
    testId?: string;
  };

const Typography = forwardRef<HTMLTypography, TypographyProps>(
  (
    { className, variant = 'text-m', affects, testId, tag = 'p', ...props },
    ref,
  ) => {
    const Comp = tag;

    return (
      <Comp
        ref={ref}
        className={cn(typographyVariants({ variant, affects, className }))}
        data-testid={testId}
        {...props}
      />
    );
  },
);

Typography.displayName = 'Typography';

export { Typography };
